import {
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  signal,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
} from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePicker } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { IconComponent } from '@navstation-portal/shared/ui/components';
import { IconNames } from '@navstation-portal/shared/data-model/ui';
import { isDate } from '@navstation-portal/reporting/data-access/vessel-report';
import { LatLongTemplateComponent } from '../lat-long-template/lat-long-template.component';

@Component({
  selector: 're-form-field',
  templateUrl: './form-field.component.html',
  styleUrl: './form-field.component.css',
  imports: [
    DatePicker,
    SelectModule,
    CheckboxModule,
    FormsModule,
    InputNumberModule,
    InputTextModule,
    LatLongTemplateComponent,
    IconComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: FormFieldComponent,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: FormFieldComponent,
      multi: true,
    },
  ],
})
export class FormFieldComponent implements ControlValueAccessor {
  IconNames = IconNames;
  _control = inject(NgControl, { optional: true });

  get isError() {
    return this._control?.control?.invalid ?? false;
  }

  positionValueChanged($event: unknown) {
    this.value.set($event);
    this.valueChange();
  }
  writeValue(): void {
    this.value.set(this.value());
  }

  registerOnChange(onChange: (value: unknown) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.update(() => isDisabled);
  }

  validate(): ValidationErrors | null {
    if (this.fieldType() === FieldType.Calendar) {
      const min = this.minDate();
      const value = this.value();

      if (!isDate(min) || !isDate(value)) return null;
      if (value >= min) return null;

      return {
        error: true,
      };
    }
    return null;
  }

  // E.S: followed the documentation for creating a ControlValueAccessor which included the empty functions

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
  onChange = (value: unknown) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched = () => {};

  id = input<string>();
  value = model<unknown>();
  minValue = input<number>(0);
  fieldLabel = input<string>();
  fieldDescription = input<string>();
  fieldType = input<FieldType>();
  fieldOptions = input<SelectOption[]>();
  useNumberGrouping = input<boolean | undefined>(true);
  fieldUnit = input<string>();
  isFieldReadOnly = input<boolean>(false);
  required = input<boolean>(false);
  labelWidthPercentage = input<number>();
  fieldWidthPercentage = computed(() => {
    const percentage = this.labelWidthPercentage();
    if (!percentage) return 100;
    return 100 - percentage;
  });
  isDisabled = input<boolean>(false);
  disabled = signal(this.isDisabled());
  minDate = input<Date | null | undefined>();
  inputFieldClasses = input<string>();
  showClearButton = input<boolean>(false);

  constructor() {
    effect(() => {
      // force revalidation if minDate changes and the
      // current value is now before the new minValue
      if (this.fieldType() !== FieldType.Calendar) return;

      const min = this.minDate();
      const value = this.value();
      if (!isDate(min) || !isDate(value)) return;
      if (value >= min) return;

      this.onChange(value);
    });
  }

  getNumberInputStyleClass() {
    const hasUnits = this.fieldUnit() !== undefined;
    const classes: string[] = [];
    if (hasUnits) {
      classes.push('!rounded-none !rounded-l-lg');
    }
    if (this.isError) {
      classes.push('border-2 border-solid border-red-500');
    }
    return classes.join(' ');
  }

  valueChange() {
    this.onChange(this.value());
  }

  fieldTouched() {
    this.onTouched();
  }
}

export enum FieldType {
  Calendar = 'Calendar',
  Select = 'Select',
  Number = 'Number',
  Position = 'Position',
  Text = 'Text',
  Toggle = 'Toggle',
  Checkbox = 'Checkbox',
}

export interface SelectOption {
  name: string;
  code: unknown;
}

export interface FormFieldConfigBase {
  fieldLabel: string;
  fieldId: string;
  fieldDescription?: string;
  isRequired?: boolean;
  isReadOnly?: boolean;
  conditions?: FormFieldCondition[];
  calculated?: boolean;
}

export interface FormFieldCondition {
  when: string; // field id to watch
  is: boolean; // value of the when field id to match against in the action
  action: FormFieldConditionAction; // what to do
  value: boolean; // value to set in the action
}

export type FormFieldConditionAction = 'setEnabled' | 'setRequired';

export interface CalendarFieldConfig extends FormFieldConfigBase {
  fieldType: FieldType.Calendar;
}

export interface PositionFieldConfig extends FormFieldConfigBase {
  fieldType: FieldType.Position;
}

export interface TextFieldConfig extends FormFieldConfigBase {
  fieldType: FieldType.Text;
}

export interface SelectFieldConfig extends FormFieldConfigBase {
  fieldType: FieldType.Select;
  fieldOptions: SelectOption[] | undefined;
}

export interface NumberFieldConfig extends FormFieldConfigBase {
  fieldType: FieldType.Number;
  fieldUnit: string | undefined;
  useNumberGrouping?: boolean | undefined;
}

export interface CheckboxFieldConfig extends FormFieldConfigBase {
  fieldType: FieldType.Checkbox;
}

export type FormFieldConfig =
  | TextFieldConfig
  | CalendarFieldConfig
  | SelectFieldConfig
  | NumberFieldConfig
  | PositionFieldConfig
  | CheckboxFieldConfig;

export type GroupedFormData = Record<string, FormFieldConfig[]>;
